import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import * as Yup from 'yup';

import Button from 'modules/views/shared/Button';
import TextField from 'modules/views/shared/TextField';
import PasswordField from 'modules/views/shared/PasswordField';

import useForm from 'hooks/useForm';
import useSubmitOnEnter from 'hooks/useSubmitOnEnter';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { createPassword, fetchWorkerDetails } from 'modules/state-mgmt/onboarding';
import useResumeProjectOnboarding from 'hooks/useResumeProjectOnboarding';
import { getCreatePasswordParams } from 'modules/views/pages/CreatePassword/utils';
import { prettifyErrorResponse } from 'utils';
import { actionMapForOtherLoaders } from 'constants/actions';
import { getPasswordRules, validatePassword } from 'utils/passwordRules';
import { ROUTES } from 'constants/routes';
import { useStyles } from './styles';

/**
 * Create Password (CH-4515).
 *
 * Shown in place of the Change Password tab for a worker who was onboarded on a project without
 * ever creating a password (a legacy of the old website/flow). There is no "Old Password" field
 * because there is no old password, which is also why this cannot reuse `ChangePassword` — that
 * screen's thunk goes through Cognito or `PUT /users/{userId}/changePassword`, and both require
 * the current password.
 */
export const CreatePassword: React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isResuming, resumeProjectOnboarding } = useResumeProjectOnboarding();
  const { t } = useTranslation();

  const { onboarding, languageCode, createPasswordLoading, fetchWorkerDetailsLoading, isAdmin } = useAppSelector(
    state => ({
      onboarding: state.onboarding,
      languageCode: state.app?.languageCode,
      createPasswordLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.createPassword],
      fetchWorkerDetailsLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.fetchWorkerDetails],
      isAdmin: state.user?.isAdmin,
    }),
  );

  // The worker's own contacts (not the admin's) — used both for the password rules and the payload.
  const email = onboarding?.email ?? '';
  const mobilePhoneNumber = onboarding?.mobilePhoneNumber ?? '';

  const createPasswordSchema = Yup.object({
    newPassword: Yup.string()
      .required(t('myProfile.createPassword.errors.newPasswordRequired'))
      .test('password-validations', t('myProfile.createPassword.errors.invalidPassword'), function (value) {
        return validatePassword(value, { email, mobilePhoneNumber }, getPasswordRules(t));
      }),
    confirmPassword: Yup.string()
      .required(t('myProfile.createPassword.errors.confirmPasswordRequired'))
      .oneOf([Yup.ref('newPassword'), null], t('myProfile.createPassword.errors.confirmPasswordMismatch')),
  });

  const handleCreatePassword = (values: { newPassword: string; confirmPassword: string }) => {
    dispatch(
      createPassword(
        getCreatePasswordParams({
          ...(onboarding || {}),
          // `inviteMethod` defaults to 0 in the onboarding slice; fall back to the channel we
          // actually hold a contact for (1 = email, 2 = phone, 3 = both).
          inviteMethod: onboarding?.inviteMethod || (email && mobilePhoneNumber ? 3 : mobilePhoneNumber ? 2 : 1),
          languageCode,
          password: values.newPassword,
          // An admin is driving this session, so it is never a worker self-registration.
          isSelfRegistered: false,
        }),
      ),
    ).then(async res => {
      if (!res.type.includes('fulfilled')) {
        enqueueSnackbar(prettifyErrorResponse((res as any).payload), { variant: 'error' });
        return;
      }

      enqueueSnackbar(t('myProfile.createPassword.successMessages.passwordCreated'), { variant: 'success' });

      try {
        // The server flips `invitationStatus` to ACCEPTED on a successful register, and that is
        // what the route guard gates Project Listing on — so re-read the worker and let the
        // server be the source of truth (the wizard's online path does the same). If this fails
        // we stay put and surface it rather than navigating into a guard bounce.
        if (onboarding?.id) {
          await dispatch(fetchWorkerDetails({ workerId: onboarding.id, languageCode })).unwrap();
        }

        // CH-4652 — a worker who verified by OTP without a password is sent here first, with the
        // resume marker still set. Now that the password exists, attach them to the project chosen
        // before the duplicate-worker check and continue onboarding. No-ops for every other way of
        // reaching this screen: the marker has to name this same worker and carry a project.
        if (await resumeProjectOnboarding(ROUTES.PROJECT_LIST.path)) return;

        navigate(ROUTES.PROJECT_LIST.path);
      } catch (error) {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      }
    });
  };

  const { values, errors, handleSubmit, setValue } = useForm({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: createPasswordSchema,
    onSubmit: handleCreatePassword,
  });

  // Keep the button busy for the whole sequence — create password *and* the worker refetch.
  const isLoading = createPasswordLoading?.isLoading || fetchWorkerDetailsLoading?.isLoading || isResuming;
  const submitOnEnter = useSubmitOnEnter(handleSubmit, isLoading);

  return (
    <Box className={classes.changePasswordContainer}>
      <Typography className={classes.title}>{t('myProfile.createPassword.title')}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PasswordField
            label={t('myProfile.createPassword.newPassword')}
            name="newPassword"
            required={true}
            value={values.newPassword}
            error={errors.newPassword?.message}
            email={email}
            mobilePhoneNumber={mobilePhoneNumber}
            passwordMatchContainerClass={classes.passwordField}
            onChange={e => setValue('newPassword', e.target.value)}
            maskText={isAdmin}
            autoComplete={isAdmin ? 'off' : 'new-password'}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('myProfile.createPassword.confirmPassword')}
            name="confirmPassword"
            required={true}
            value={values.confirmPassword}
            error={errors.confirmPassword?.message}
            type={isAdmin ? 'text' : 'password'}
            maskText={isAdmin}
            onChange={e => setValue('confirmPassword', e.target.value)}
            onKeyDown={submitOnEnter}
            autoComplete={isAdmin ? 'off' : 'new-password'}
          />
        </Grid>
      </Grid>
      <div className={classes.buttonContainer}>
        <Button
          label={t('myProfile.createPassword.buttons.createPassword')}
          isLoading={isLoading}
          styleClass={classes.saveButton}
          onClick={handleSubmit}
        />
      </div>
    </Box>
  );
};
